import React, { useEffect, useState } from 'react';
import {
  listBundlesAdmin,
  saveBundleAdmin,
  deleteBundleAdmin,
  listPromosAdmin,
  savePromoAdmin,
  deletePromoAdmin,
  type Bundle,
  type Promo,
} from '../../lib/commerce';
import { EmptyState, Panel, PanelHead, Stamp, fieldCls, labelCls } from '../../components/lms/ui';
import { ListRowsSkeleton } from '../../components/lms/Skeletons';

const CommerceAdmin: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [promos, setPromos] = useState<Promo[]>([]);
  const [bundleForm, setBundleForm] = useState({ id: '', title: '', price_value: 0, compare_at_price: 0, course_ids: '', description: '' });
  const [promoForm, setPromoForm] = useState({ code: '', percent_off: 10, course_id: '' });
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [bundleRows, promoRows] = await Promise.all([
        listBundlesAdmin().catch(() => []),
        listPromosAdmin().catch(() => []),
      ]);
      setBundles(bundleRows);
      setPromos(promoRows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const saveBundle = async () => {
    if (!bundleForm.id || !bundleForm.title) {
      setError('A bundle needs an id and a title before it can be saved.');
      return;
    }
    setBusy('bundle');
    setError('');
    try {
      await saveBundleAdmin({
        id: bundleForm.id,
        slug: bundleForm.id,
        title: bundleForm.title,
        description: bundleForm.description,
        course_ids: bundleForm.course_ids.split(',').map((s) => s.trim()).filter(Boolean),
        price_value: bundleForm.price_value,
        compare_at_price: bundleForm.compare_at_price,
        is_published: true,
      });
      setBundleForm({ id: '', title: '', price_value: 0, compare_at_price: 0, course_ids: '', description: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the bundle.');
    } finally {
      setBusy('');
    }
  };

  const savePromo = async () => {
    if (!promoForm.code) {
      setError('Enter a promo code before saving.');
      return;
    }
    setBusy('promo');
    setError('');
    try {
      await savePromoAdmin({ code: promoForm.code, percent_off: promoForm.percent_off, course_id: promoForm.course_id || null });
      setPromoForm({ code: '', percent_off: 10, course_id: '' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the promo code.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-6 space-y-4">
      {error && (
        <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{error}</p>
      )}

      <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
        <Panel>
          <PanelHead
            title="Grouped pathways"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{bundles.length} on file</span>}
          />
          <form
            className="space-y-4 px-5 py-4"
            onSubmit={(e) => { e.preventDefault(); void saveBundle(); }}
          >
            <p className="max-w-[62ch] text-[12px] leading-5 text-ink-2">
              A bundle sells several programmes as one payment. Course IDs are comma-separated (for example 4,5); saved bundles publish to the programme register immediately.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="bundle-id" className={labelCls}>Bundle id</label>
                <input
                  id="bundle-id"
                  value={bundleForm.id}
                  onChange={(e) => setBundleForm({ ...bundleForm, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-') })}
                  placeholder="bundle-diploma-track"
                  className={`${fieldCls} font-mono !text-[12px]`}
                />
              </div>
              <div>
                <label htmlFor="bundle-title" className={labelCls}>Title</label>
                <input
                  id="bundle-title"
                  value={bundleForm.title}
                  onChange={(e) => setBundleForm({ ...bundleForm, title: e.target.value })}
                  placeholder="Diploma Track Bundle"
                  className={fieldCls}
                />
              </div>
            </div>

            <div>
              <label htmlFor="bundle-description" className={labelCls}>Description</label>
              <input
                id="bundle-description"
                value={bundleForm.description}
                onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })}
                placeholder="What the bundle covers and who it suits"
                className={fieldCls}
              />
            </div>

            <div>
              <label htmlFor="bundle-courses" className={labelCls}>Course IDs</label>
              <input
                id="bundle-courses"
                value={bundleForm.course_ids}
                onChange={(e) => setBundleForm({ ...bundleForm, course_ids: e.target.value })}
                placeholder="4, 5"
                className={`${fieldCls} font-mono !text-[12px]`}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="bundle-price" className={labelCls}>Bundle price (NGN)</label>
                <input
                  id="bundle-price"
                  type="number"
                  value={bundleForm.price_value}
                  onChange={(e) => setBundleForm({ ...bundleForm, price_value: Number(e.target.value) })}
                  className={`${fieldCls} tnum`}
                />
              </div>
              <div>
                <label htmlFor="bundle-compare" className={labelCls}>Compare-at price (NGN)</label>
                <input
                  id="bundle-compare"
                  type="number"
                  value={bundleForm.compare_at_price}
                  onChange={(e) => setBundleForm({ ...bundleForm, compare_at_price: Number(e.target.value) })}
                  className={`${fieldCls} tnum`}
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-rule pt-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Publishes on save</p>
              <button type="submit" disabled={busy === 'bundle'} className="lms-btn-primary !px-4 !py-2 !text-[12px]">
                {busy === 'bundle' ? 'Saving…' : 'Save bundle'}
              </button>
            </div>
          </form>

          <div className="border-t border-rule">
            <PanelHead title="Bundle ledger" />
            {loading ? (
              <ListRowsSkeleton rows={2} label="Loading bundles" />
            ) : bundles.length === 0 ? (
              <EmptyState title="No bundles on file" body="Create the first grouped pathway above. It appears on the programme register as soon as it saves." />
            ) : (
              <ul className="divide-y divide-rule">
                {bundles.map((b) => (
                  <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[13px] font-medium text-ink">{b.title}</p>
                        <Stamp tone={b.is_published ? 'cleared' : 'neutral'}>{b.is_published ? 'Live' : 'Draft'}</Stamp>
                      </div>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                        {b.id} · ₦{b.price_value.toLocaleString('en-NG')} · {b.course_ids.join(', ') || 'No courses'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`Delete ${b.title}?`)) return;
                        await deleteBundleAdmin(b.id);
                        await load();
                      }}
                      className="lms-btn-quiet !px-2.5 !py-1.5 !text-[11px]"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        <Panel>
          <PanelHead
            title="Promo codes"
            action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{promos.length} issued</span>}
          />
          <form
            className="space-y-4 px-5 py-4"
            onSubmit={(e) => { e.preventDefault(); void savePromo(); }}
          >
            <p className="max-w-[62ch] text-[12px] leading-5 text-ink-2">
              Percent-off codes, optionally locked to one course. Codes are checked against Paystack checkout before a student is enrolled.
            </p>

            <div>
              <label htmlFor="promo-code" className={labelCls}>Code</label>
              <input
                id="promo-code"
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') })}
                placeholder="LAUNCH20"
                className={`${fieldCls} font-mono uppercase tracking-[0.08em]`}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="promo-percent" className={labelCls}>Percent off</label>
                <input
                  id="promo-percent"
                  type="number"
                  min={1}
                  max={100}
                  value={promoForm.percent_off}
                  onChange={(e) => setPromoForm({ ...promoForm, percent_off: Number(e.target.value) })}
                  className={`${fieldCls} tnum`}
                />
              </div>
              <div>
                <label htmlFor="promo-course" className={labelCls}>Course ID (optional)</label>
                <input
                  id="promo-course"
                  value={promoForm.course_id}
                  onChange={(e) => setPromoForm({ ...promoForm, course_id: e.target.value })}
                  placeholder="All courses"
                  className={`${fieldCls} font-mono !text-[12px]`}
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-rule pt-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Active on save</p>
              <button type="submit" disabled={busy === 'promo'} className="lms-btn-primary !px-4 !py-2 !text-[12px]">
                {busy === 'promo' ? 'Saving…' : 'Save code'}
              </button>
            </div>
          </form>

          <div className="border-t border-rule">
            <PanelHead title="Code ledger" />
            {loading ? (
              <ListRowsSkeleton rows={2} label="Loading promo codes" />
            ) : promos.length === 0 ? (
              <EmptyState title="No promo codes issued" body="Create a code above. Students enter it at checkout to reduce the programme price." />
            ) : (
              <ul className="divide-y divide-rule">
                {promos.map((p) => (
                  <li key={p.code} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-mono text-[13px] font-semibold uppercase tracking-[0.08em] text-ink">{p.code}</p>
                        <Stamp tone={p.active ? 'cleared' : 'neutral'}>{p.active ? 'Active' : 'Off'}</Stamp>
                      </div>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                        {p.percent_off}% off · {p.course_id || 'all courses'} · used {p.uses}{p.max_uses ? `/${p.max_uses}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        await deletePromoAdmin(p.code);
                        await load();
                      }}
                      className="lms-btn-quiet !px-2.5 !py-1.5 !text-[11px]"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default CommerceAdmin;
